/**
 * Webhook Form - Create/edit webhook configuration
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Webhook, CreateWebhookRequest } from '@/services/api/webhooks';

interface EventType {
  value: string;
  label: string;
}

interface WebhookFormProps {
  webhook: Webhook | null;
  eventTypes: EventType[];
  onSave: (data: CreateWebhookRequest) => Promise<void>;
  onCancel: () => void;
}

export const WebhookForm: React.FC<WebhookFormProps> = ({
  webhook,
  eventTypes,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [url, setUrl] = useState(webhook?.url || '');
  const [description, setDescription] = useState(webhook?.description || '');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(webhook?.events || []);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxRetries, setMaxRetries] = useState(webhook?.maxRetries ?? 3);
  const [customHeaders, setCustomHeaders] = useState<{ key: string; value: string }[]>(
    webhook?.headers
      ? Object.entries(webhook.headers).map(([key, value]) => ({ key, value }))
      : []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate URL
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    // Build headers object
    const headers: Record<string, string> = {};
    customHeaders.forEach(({ key, value }) => {
      if (key.trim() && value.trim()) {
        headers[key.trim()] = value.trim();
      }
    });

    try {
      setSaving(true);
      const data: CreateWebhookRequest = {
        url,
        description,
        events: selectedEvents,
        maxRetries,
      };
      data.headers = headers;
      await onSave(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to save webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleEventToggle = (eventValue: string) => {
    if (selectedEvents.includes(eventValue)) {
      setSelectedEvents(selectedEvents.filter((e) => e !== eventValue));
    } else {
      setSelectedEvents([...selectedEvents, eventValue]);
    }
  };

  const handleSelectAll = () => {
    if (selectedEvents.length === eventTypes.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(eventTypes.map((e) => e.value));
    }
  };

  const handleAddHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const handleRemoveHeader = (index: number) => {
    setCustomHeaders(customHeaders.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...customHeaders];
    const item = updated[index];
    if (item) {
      item[field] = value;
    }
    setCustomHeaders(updated);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* URL */}
        <div>
          <label htmlFor="webhook-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('settings.webhooks.form.url', 'Payload URL')} *
          </label>
          <input
            id="webhook-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/webhook"
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.webhooks.form.urlHint', 'We will send POST requests to this URL')}
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="webhook-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('settings.webhooks.form.description', 'Description')}
          </label>
          <input
            type="text"
            id="webhook-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Events */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.webhooks.form.events', 'Events')}
            </label>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {selectedEvents.length === eventTypes.length
                ? t('settings.webhooks.form.deselectAll', 'Deselect all')
                : t('settings.webhooks.form.selectAll', 'Select all')}
            </button>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
            <div className="grid grid-cols-2 gap-1 p-2">
              {eventTypes.map((event) => (
                <label
                  key={event.value}
                  className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event.value)}
                    onChange={() => handleEventToggle(event.value)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{event.label}</span>
                </label>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {selectedEvents.length === 0
              ? t('settings.webhooks.form.allEvents', 'All events will be sent')
              : t('settings.webhooks.form.selectedEvents', '{{count}} event(s) selected', {
                  count: selectedEvents.length,
                })}
          </p>
        </div>

        {/* Advanced Settings */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {t('settings.webhooks.form.advanced', 'Advanced Settings')}
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4 pl-6">
              {/* Retry Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.webhooks.form.maxRetries', 'Max Retries')}
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={maxRetries}
                    onChange={(e) => setMaxRetries(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

              </div>

              {/* Custom Headers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.webhooks.form.customHeaders', 'Custom Headers')}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddHeader}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    {t('settings.webhooks.form.addHeader', '+ Add Header')}
                  </button>
                </div>
                {customHeaders.length > 0 && (
                  <div className="space-y-2">
                    {customHeaders.map((header, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={header.key}
                          onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                          placeholder="Header name"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <input
                          type="text"
                          value={header.value}
                          onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                          placeholder="Value"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveHeader(index)}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving
            ? t('common.saving', 'Saving...')
            : webhook
            ? t('common.save', 'Save')
            : t('common.create', 'Create')}
        </button>
      </div>
    </form>
  );
};

export default WebhookForm;
